import type { TeamTaskEntity } from "@/modules/boxDashboard/modules/TeamTask/entities/teamTaskEntity"
import type { BoxStaffItemEntity } from "@modules/boxStaff/entities/boxStaffEntity"

export type BoxStaffModel = {
  id: string
  fullName: string
  initials: string
  avatarUrl: string | null
}

export type TeamTaskModel = {
  id: string
  content: string
  isDone: boolean
  isCreated: boolean
  createdAt: string
  createdBy: BoxStaffModel
  completedAt: string | null
  completedBy: BoxStaffModel | null
  isArchived: boolean
}

export type TeamTasksModel = {
  tasks: TeamTaskModel[]
  hasTasks: boolean
  hasCompletedTasks: boolean
  canEditTask: boolean
  canToggleTask: boolean
  canAddTask: boolean
  title: "boxDashboard.teamTasks.completedList" | "boxDashboard.teamTasks.title"
}

function createBoxStaffModel(staff: BoxStaffItemEntity): BoxStaffModel {
  return {
    id: staff.id,
    fullName: staff.getFullName(),
    initials: staff.getInitials(),
    avatarUrl: staff.avatar?.url ?? null,
  }
}

function createTeamTaskModel(task: TeamTaskEntity): TeamTaskModel {
  return {
    id: task.id,
    content: task.task,
    isDone: task.isCompleted(),
    isCreated: task.isCreated(),
    createdAt: task.getCreatedDateFormatted(),
    createdBy: createBoxStaffModel(task.creator),
    completedAt: task.getCompletedDateFormatted(),
    completedBy: task.completedBy ? createBoxStaffModel(task.completedBy) : null,
    isArchived: task.isArchived(),
  }
}

export type CreateTeamTasksModelParams = {
  activeTasks: TeamTaskEntity[]
  completedTasks: TeamTaskEntity[]
  showCompleted: boolean
  canEditDashboard: boolean
}

export function createTeamTasksModel(params: CreateTeamTasksModelParams): TeamTasksModel {
  const tasks = params.showCompleted ? params.completedTasks : params.activeTasks
  const taskModels = tasks.map(createTeamTaskModel)

  const canEdit = params.canEditDashboard
  const canManageTasks = canEdit && !params.showCompleted

  return {
    tasks: taskModels,
    hasTasks: taskModels.length > 0,
    hasCompletedTasks: params.completedTasks.length > 0,
    canEditTask: canManageTasks,
    canToggleTask: canManageTasks,
    canAddTask: canManageTasks,
    title: params.showCompleted ? "boxDashboard.teamTasks.completedList" : "boxDashboard.teamTasks.title",
  }
}
